import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useSelector, useDispatch} from 'react-redux';

import {useInflight} from 'src/hooks/redux';
import {getJobScoreCards, getInterviewFlow} from 'src/action-creators/crm';

import {
  selectApplicantInformation,
  selectJobInterviewFlow,
  selectJobScoreCards,
  selectUserInfo,
  selectHasCrmPermission,
} from 'src/selectors/crm';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {BodyMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';

import SingleEvaluation from 'src/components/crm/lib/candidate-evaluation/single-evaluation';

import * as reduxApi from 'src/utils/redux-api-v2';

import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import css from './feedback.css';


const ApplicantFeedbacks = ({
  jobId,
  reloadCandidateData,
}: {
  jobId: number;
  reloadCandidateData: () => void;
}) => {
  const dispatch = useDispatch();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const applicantDetails = useSelector(selectApplicantInformation);
  const [loadingPreviousJobFeedback, setLoadingPreviousJobFeedback] =
    React.useState(false);
  const [previousJobFeedback, setPreviousJobFeedback] =
    React.useState<any>(null);
  const [accordionCollapseState, setAccordionCollapseState] = React.useState({
    currentJob: true,
    pastJob: false,
  });

  const currentApplication = applicantDetails?.applications?.find(
    (app: any) => app?.jobId === jobId,
  );

  const isInterviewFlowDataLoading = useInflight(
    getInterviewFlow(jobId.toString(), applicantDetails?.id?.toString()),
  );
  const interviewFlowData = useSelector(selectJobInterviewFlow);
  const jobStages = interviewFlowData?.stages ?? [];

  const {stepCandidates = []} = currentApplication ?? {};

  const hasFeedbacks = React.useMemo(() => {
    if (!stepCandidates || stepCandidates.length === 0) {
      return false;
    }

    return stepCandidates.some((stepCandidate) => {
      // @ts-expect-error - TS2339 - Property 'interviewReviews' does not exist on type 'ApplicationStepCandidate'. | TS2339 - Property 'assessmentCandidate' does not exist on type 'ApplicationStepCandidate'.
      const {interviewReviews = [], assessmentCandidate} = stepCandidate || {};
      const assesmentReports = assessmentCandidate
        ? [assessmentCandidate.report]
        : [];
      return (
        interviewReviews.length > 0 || assesmentReports.some((report) => report)
      );
    });
  }, [stepCandidates]);

  const isJobScoreCardDataLoading = useInflight(getJobScoreCards(jobId));
  const jobScoreCardData = useSelector(selectJobScoreCards);

  const toggleAccordionCollapseState = (key: string) => {
    setAccordionCollapseState((prev) => ({
      ...prev,
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ currentJob: boolean; pastJob: boolean; }'.
      [key]: !accordionCollapseState[key],
    }));
  };

  React.useEffect(() => {
    const fetchApplicantPreviousFeedback = async () => {
      setLoadingPreviousJobFeedback(true);
      await dispatch(
        reduxApi.get(
          `api/relogged-feedback?jobId=${jobId}&candidateId=${applicantDetails?.id}`,
          {},
          {apiPath: '/trmapi/'},
        ),
      )
        // @ts-expect-error - TS2339 - Property 'then' does not exist on type 'ThunkAction<any>'. | TS7006 - Parameter 'data' implicitly has an 'any' type.
        .then((data) => setPreviousJobFeedback(data))
        .finally(() => {
          setLoadingPreviousJobFeedback(false);
        });
    };

    fetchApplicantPreviousFeedback();
  }, [jobId, applicantDetails?.id]);

  return (
    <div className={css.applicantFeedbackContainer}>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader
          // @ts-expect-error - TS2322 - Type '{ children: Element[]; onClick: () => void; className: string; }' is not assignable to type 'IntrinsicAttributes & CardChildProps'.
          onClick={() => toggleAccordionCollapseState('currentJob')}
          className={css.accordionCardHeader}
        >
          <CardTitle>{t('From_Current_job', 'From Current job')}</CardTitle>
          <CardActions>
            <Button
              size="small"
              type="ghost"
              iconLeftName={
                accordionCollapseState.currentJob
                  ? 'chevron-up'
                  : 'chevron-down'
              }
              onClick={() => toggleAccordionCollapseState('currentJob')}
            />
          </CardActions>
        </CardHeader>
        {accordionCollapseState.currentJob ? (
          <CardContent className={css.cardContent}>
            {hasFeedbacks ? (
              <>
                {isInterviewFlowDataLoading || isJobScoreCardDataLoading ? (
                  <Shimmer width="100%" height="100px" />
                ) : (
                  <div className={css.currentJobCardContentContainer}>
                    {stepCandidates?.map((stepCandidate: any) => (
                      <StepCandidateFeedbackWrapper
                        stepCandidate={stepCandidate}
                        jobStages={jobStages}
                        scorecard={jobScoreCardData}
                        reloadCandidateData={reloadCandidateData}
                        jobId={jobId}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <BodyMedium>
                {t(
                  'No_feedbacks_has_been_submitted_for_this_candidate_yet',
                  'No feedbacks has been submitted for this candidate yet',
                )}
              </BodyMedium>
            )}
          </CardContent>
        ) : null}
      </Card>
      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader
          // @ts-expect-error - TS2322 - Type '{ children: Element[]; onClick: () => void; className: string; }' is not assignable to type 'IntrinsicAttributes & CardChildProps'.
          onClick={() => toggleAccordionCollapseState('pastJob')}
          className={css.accordionCardHeader}
        >
          <CardTitle>{t('From_Past_job', 'From Past job')}</CardTitle>
          <CardActions>
            <Button
              size="small"
              type="ghost"
              iconLeftName={
                accordionCollapseState.pastJob ? 'chevron-up' : 'chevron-down'
              }
              onClick={() => toggleAccordionCollapseState('pastJob')}
            />
          </CardActions>
        </CardHeader>
        {accordionCollapseState.pastJob ? (
          <CardContent className={css.cardContent}>
            {loadingPreviousJobFeedback ? (
              <CircularLoader />
            ) : previousJobFeedback ? (
              <iframe
                title="resume-iframe"
                className="doc"
                src={previousJobFeedback?.publicUrl}
                height="1000px"
                width="100%"
              />
            ) : (
              <BodyMedium>
                {t(
                  'Previous_job\'s_feedback_is_not_available',
                  'Previous job\'s feedback is not available',
                )}
              </BodyMedium>
            )}
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
};

export default ApplicantFeedbacks;

const StepCandidateFeedbackWrapper = ({
  // @ts-expect-error - TS7031 - Binding element 'stepCandidate' implicitly has an 'any' type.
  stepCandidate,
  // @ts-expect-error - TS7031 - Binding element 'jobStages' implicitly has an 'any' type.
  jobStages,
  // @ts-expect-error - TS7031 - Binding element 'scorecard' implicitly has an 'any' type.
  scorecard,
  // @ts-expect-error - TS7031 - Binding element 'reloadCandidateData' implicitly has an 'any' type.
  reloadCandidateData,
  // @ts-expect-error - TS7031 - Binding element 'jobId' implicitly has an 'any' type.
  jobId,
}) => {
  const user = useSelector(selectUserInfo);
  const {user: userInfo} = user ?? {};

  const {
    interviewReviews = [],
    assessmentCandidate,
    stepId,
    stageId,
  } = stepCandidate || {};
  const assesmentReports = assessmentCandidate
    ? [{...assessmentCandidate.report, stepId, stageId}]
    : [];

  let finalInterviewReviews = [...interviewReviews, ...assesmentReports];

  const viewOthersFeedback = selectHasCrmPermission(
    user,
    CRM_PERMISSIONS.VIEW_OTHERS_FEEDBACK,
  );
  if (!viewOthersFeedback) {
    finalInterviewReviews = finalInterviewReviews.filter(
      (review) => review.reviewerId === userInfo?.id,
    );
  }

  if (finalInterviewReviews.length === 0) {
    return null;
  }

  return (
    <>
      {finalInterviewReviews
        .sort((a, b) => a.createdOn - b.createdOn)
        .map((review) => {
          const stage = jobStages?.find((stage: any) => {
            return stage.id === review.stageId;
          });
          return (
            // @ts-expect-error - TS2741 - Property 'scorecardConfigData' is missing in type '{ key: any; review: any; stepCandidate: any; scorecard: any; stage: any; onUpdate: any; jobId: any; }' but required in type '{ review: any; stepCandidate: any; stage: any; scorecard: any; scorecardConfigData: any; hideDelete?: boolean | undefined; jobId: any; onUpdate: any; }'.
            <SingleEvaluation
              key={review.id}
              review={review}
              stepCandidate={stepCandidate}
              scorecard={scorecard}
              stage={stage}
              onUpdate={reloadCandidateData}
              jobId={jobId}
            />
          );
        })}
    </>
  );
};
